/**
 * Saved mental models — the client half of migration 810.
 *
 * A signed-in person keeps a named model in one of their Spaces: their own
 * personal space or a team space they belong to. Reads go through the table
 * (row security = active membership of the space); every write is a function.
 */

import { getMySpacesResult, ensurePersonalSpace } from '@/lib/spaceService';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '@/lib/supabaseSync';
import { parseMapDoc, type MapDoc, type MapLevelNumber } from './model';

export interface SaveSpace {
  id: string;
  name: string;
  kind: 'personal' | 'team';
  /** False for viewers and guests of a team space: they can read, not save. */
  canSave: boolean;
}

export interface SavedModel {
  id: string;
  spaceId: string;
  name: string;
  level: MapLevelNumber | null;
  doc: MapDoc;
  createdBy: string | null;
  updatedAt: string;
}

export interface SavedResult<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

const WRITE_ROLES = new Set(['owner', 'admin', 'member']);

/** Order and label the spaces a person can choose between: their own first, then teams by name. */
export function toSaveSpaces(
  spaces: { id: string; name: string; spaceType: string; memberRole: string }[],
): SaveSpace[] {
  return spaces
    .filter((s) => s.spaceType === 'personal' || s.spaceType === 'team')
    .map((s) => ({
      id: s.id,
      name: s.spaceType === 'personal' ? 'My own space' : s.name,
      kind: s.spaceType as 'personal' | 'team',
      canSave: WRITE_ROLES.has(s.memberRole),
    }))
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'personal' ? -1 : 1));
}

/** The caller's own space and team spaces in the current workspace. */
export async function listSaveSpaces(): Promise<SavedResult<SaveSpace[]>> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) {
    return { ok: false, error: 'Open Priorities.ai and pick a workspace first, then come back to save.' };
  }
  // A person who has never opened the workspace's spaces has no personal space yet.
  await ensurePersonalSpace(workspaceId);
  const result = await getMySpacesResult(workspaceId);
  if (!result.ok) return { ok: false, error: 'Your spaces could not be loaded. Try again.' };
  return { ok: true, data: toSaveSpaces(result.spaces) };
}

function parseSavedModel(value: unknown): SavedModel | null {
  if (!value || typeof value !== 'object') return null;
  const r = value as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.space_id !== 'string' || typeof r.name !== 'string') return null;
  return {
    id: r.id,
    spaceId: r.space_id,
    name: r.name,
    level: r.level === 1 || r.level === 2 || r.level === 3 || r.level === 4 ? r.level : null,
    doc: parseMapDoc(r.doc),
    createdBy: typeof r.created_by === 'string' ? r.created_by : null,
    updatedAt: typeof r.updated_at === 'string' ? r.updated_at : '',
  };
}

const COLUMNS = 'id, space_id, name, level, doc, created_by, updated_at';

/** Every saved model the caller can see in the current workspace, newest first. */
export async function listSavedModels(): Promise<SavedResult<SavedModel[]>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };
  const workspaceId = getCurrentWorkspaceId();
  let query = supabase.from('mental_models').select(COLUMNS).is('retired_at', null).order('updated_at', { ascending: false }).limit(200);
  if (workspaceId) query = query.eq('workspace_id', workspaceId);
  const { data, error } = await query;
  if (error) return { ok: false, error: error.message };
  const models: SavedModel[] = [];
  for (const row of (data ?? []) as unknown[]) {
    const model = parseSavedModel(row);
    if (model) models.push(model);
  }
  return { ok: true, data: models };
}

export async function getSavedModel(id: string): Promise<SavedResult<SavedModel>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };
  const { data, error } = await supabase.from('mental_models').select(COLUMNS).eq('id', id).is('retired_at', null).maybeSingle();
  if (error) return { ok: false, error: error.message };
  const model = parseSavedModel(data);
  return model ? { ok: true, data: model } : { ok: false, error: 'This model is not here, or you are not in its space.' };
}

export interface SaveModelInput {
  /** Omit to save a new model. */
  modelId?: string;
  /** Required for a new model; on an existing one, moves it. */
  spaceId?: string;
  name?: string;
  doc?: MapDoc;
  level?: MapLevelNumber | null;
}

/** Save a new model or changes to an existing one. Resolves to the model's id. */
export async function saveSavedModel(input: SaveModelInput): Promise<SavedResult<string>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };
  const { data, error } = await supabase.rpc('save_mental_model', {
    p_model_id: input.modelId ?? null,
    p_space_id: input.spaceId ?? null,
    p_name: input.name ?? null,
    p_doc: input.doc ?? null,
    p_level: input.level ?? null,
  });
  if (error) return { ok: false, error: error.message || 'The model could not be saved.' };
  const id = (data as { model_id?: unknown } | null)?.model_id;
  return typeof id === 'string' ? { ok: true, data: id } : { ok: false, error: 'The model could not be saved.' };
}

/** Remove a model from view (it is kept, and can be restored) or bring it back. */
export async function setSavedModelRetired(modelId: string, retired: boolean): Promise<SavedResult<true>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };
  const { error } = await supabase.rpc('set_mental_model_retired', { p_model_id: modelId, p_retired: retired });
  return error ? { ok: false, error: error.message } : { ok: true, data: true };
}

export function savedModelPath(modelId: string): string {
  return `/elevation/lab/mental-map/model/${modelId}`;
}
